// libraries
import React from 'react'
import { noop } from 'lodash'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { SwipeableDrawer, Stack, Divider, Button, Box, ToggleButtonGroup, ToggleButton } from '@mui/material'

// generic components
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import GrossTareImageGrid from 'components/generic/image-preview/GrossTareImageGrid'

// helpers and utils
import { IMAGE_TYPES } from 'utils/constants'

const MultiImageBlade = ({
  multiImageBladeData,
  setMultiImageBladeData,
  generateSaveImagesFunction,
  disabledUploadImages,
  ...restProps
}) => {
  const { t } = useTranslation('inboundTicket')

  const isAxle = !!multiImageBladeData?.axleNumber

  return (
    <SwipeableDrawer
      anchor="right"
      open={!!multiImageBladeData}
      onOpen={noop}
      onClose={() => setMultiImageBladeData(null)}
      PaperProps={{ sx: { maxWidth: '512px', width: '100%' } }}
      disableSwipeToOpen
      {...restProps}
    >
      <Stack height="100vh">
        <FormModalHeading
          title={
            multiImageBladeData?.materialName ||
            multiImageBladeData?.name ||
            (isAxle && t('axle_images')) ||
            (multiImageBladeData?.id === 'truck-total' && t('truck_images')) ||
            ''
          }
          backButtonPosition="right"
          onBack={() => setMultiImageBladeData(null)}
          statusChip={
            isAxle && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <ToggleButtonGroup
                  value={multiImageBladeData?.axleNumber}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      const newAxleData = multiImageBladeData.axleWeights?.find(
                        axleData => axleData.axleNumber === value,
                      )
                      setMultiImageBladeData(prevMultiImageBladeData => ({
                        ...prevMultiImageBladeData,
                        axleNumber: value,
                        gross: newAxleData?.gross || 0,
                        tare: newAxleData?.tare || 0,
                        grossImages: newAxleData?.grossImages || [],
                        tareImages: newAxleData?.tareImages || [],
                      }))
                    }
                  }}
                >
                  {[...Array(multiImageBladeData?.axleWeights?.length || 2).keys()].map(number => (
                    <ToggleButton sx={{ width: '48px' }} key={`axle-${number + 1}-toggle`} value={number + 1}>
                      {number + 1}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            )
          }
        />
        <Divider />
        <Box px={4} py={2} flexGrow={1}>
          <GrossTareImageGrid
            title="Gross"
            weight={Number(multiImageBladeData?.gross || multiImageBladeData?.yardGross || 0)}
            images={multiImageBladeData?.grossImages || []}
            saveImages={generateSaveImagesFunction(IMAGE_TYPES.GROSS)}
            hideButton={disabledUploadImages}
          />
          <GrossTareImageGrid
            title="Tare"
            weight={Number(multiImageBladeData?.tare || multiImageBladeData?.yardTare || 0)}
            images={multiImageBladeData?.tareImages || []}
            saveImages={generateSaveImagesFunction(IMAGE_TYPES.TARE)}
            hideButton={disabledUploadImages}
          />
        </Box>
        <Divider />
        <Stack direction="row" p={2} spacing={2}>
          <Button variant="contained" onClick={() => setMultiImageBladeData(null)}>
            Done
          </Button>
        </Stack>
      </Stack>
    </SwipeableDrawer>
  )
}

MultiImageBlade.propTypes = {
  multiImageBladeData: PropTypes.object,
  setMultiImageBladeData: PropTypes.func.isRequired,
  generateSaveImagesFunction: PropTypes.func.isRequired,
  disabledUploadImages: PropTypes.bool,
}

MultiImageBlade.defaultProps = {
  multiImageBladeData: null,
  disabledUploadImages: false,
}

export default MultiImageBlade
